import { useQuery } from "@tanstack/react-query";
import { useServices } from "../hooks/use-services";
import { useState } from "react";
import { authInterceptor } from '../lib/auth-interceptor';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Trash2, Edit } from "lucide-react";
import { useToast } from '../hooks/use-toast';
import { Link } from "wouter";
import { MEASUREMENT_UNITS } from '../lib/service-types';
import Sidebar from '../components/layout/sidebar';
import MobileSidebar from '../components/layout/mobile-sidebar';
import TopNav from '../components/layout/top-nav';
import { Label } from '../components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

interface Service {
  id: string;
  name: string;
  serviceType: string;
  unit: string;
  laborRate: number;
  laborMethod: string;
}

export default function SimplePricingPage() {
  const [newService, setNewService] = useState({
    name: "",
    serviceType: "",
    unit: "sqft",
    laborRate: 0,
    laborMethod: "by_area"
  });

  const [editingService, setEditingService] = useState<Service | null>(null);
  
  // Delete confirmation dialog state
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    serviceId: string | number | null;
    serviceName: string;
  }>({
    isOpen: false,
    serviceId: null,
    serviceName: ''
  });

  // Get services from database using direct endpoint
  const { services, refetch, isLoading } = useServices();
  const { toast } = useToast();

  const handleSaveService = async () => {
    try {
      const serviceData = editingService ? newService : newService;
      
      if (!serviceData.name || !serviceData.serviceType) {
        toast({
          title: "Missing Information",
          description: "Please fill in service name and type",
          variant: "destructive",
        });
        return;
      }

      // Note: Removed duplicate service type check
      // Contractors should be able to create multiple custom services with the same type

      if (editingService) {
        const response = await fetch(`/api/direct/services`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...authInterceptor.addAuthHeaders()
          },
          body: JSON.stringify({
            id: editingService.id,
            name: serviceData.name,
            serviceType: serviceData.serviceType,
            unit: serviceData.unit,
            laborRate: serviceData.laborRate,
            laborMethod: serviceData.laborMethod
          })
        });

        if (response.ok) {
          setEditingService(null);
          resetForm();
          refetch();
          toast({
            title: "Service Updated",
            description: "Service has been updated successfully",
          });
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to update service');
        }
      } else {
        // Create new service
        const response = await fetch('/api/direct/services', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authInterceptor.addAuthHeaders()
          },
          body: JSON.stringify(serviceData)
        });

        if (response.ok) {
          resetForm();
          refetch();
          toast({
            title: "Service Added",
            description: "New service has been saved successfully",
          });
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || errorData.error || 'Failed to save service');
        }
      }
    } catch (error) {
      console.error('Error saving service:', error);
      toast({
        title: "Error saving",
        description: error instanceof Error ? error.message : "Could not save service. Please try again.",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setNewService({
      name: "",
      serviceType: "",
      unit: "sqft",
      laborRate: 0,
      laborMethod: "by_area"
    });
    setEditingService(null);
  };

  const handleEditService = (service: any) => {
    if (!service.id) {
      toast({
        title: "Error",
        description: "Service ID is missing. Cannot edit this service.",
        variant: "destructive",
      });
      return;
    }
    
    // Create a properly formatted service object
    const formattedService: Service = {
      id: service.id,
      name: service.label || service.name || '',
      serviceType: service.value || service.serviceType || '',
      unit: service.unit || '',
      laborRate: service.laborRate || 0,
      laborMethod: service.laborMethod || 'by_area'
    };
    
    setEditingService(formattedService);
    setNewService({
      name: service.label || service.name || '',
      serviceType: service.value || service.serviceType || '',
      unit: service.unit || 'ft',
      laborRate: Number(service.laborRate) || 0,
      laborMethod: service.laborMethod || 'by_area'
    });
  };

  const showDeleteConfirmation = (service: Service) => {
    if (!service.id) {
      console.error('❌ CRITICAL: Cannot delete service - ID is missing!', service);
      toast({
        title: "Error",
        description: "Service ID is missing. Cannot delete this service.",
        variant: "destructive",
      });
      return;
    }
    
    setDeleteDialog({
      isOpen: true,
      serviceId: service.id,
      serviceName: service.label || 'Unknown Service'
    });
  };

  const confirmDelete = async () => {
    if (!deleteDialog.serviceId) return;
    try {
      const headers = { 'Content-Type': 'application/json', ...authInterceptor.addAuthHeaders() };
      const response = await fetch(`/api/direct/services`, { method: 'DELETE', headers, body: JSON.stringify({ id: deleteDialog.serviceId }) });
      if (response.ok) {
        await response.json();
        refetch();
        toast({
          title: "Service Deleted",
          description: `${deleteDialog.serviceName} has been removed successfully`,
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to delete service (${response.status})`);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Could not delete service. Please try again.",
        variant: "destructive",
      });
    } finally {
      // Close the dialog
      setDeleteDialog({
        isOpen: false,
        serviceId: null,
        serviceName: ''
      });
    }
  };

  return (
    <div className="remodra-layout">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <MobileSidebar />
      <div className="remodra-main-mobile md:ml-64">
        <TopNav />
        <div className="remodra-content">
          <main className="dashboard-2025 bg-[var(--dash-bg)] min-h-screen p-6 md:p-8 pt-24 flex flex-col items-center">
            <div className="w-full space-y-6">
              <div>
                <h1 className="text-2xl font-bold text-[var(--dash-text)]">Services</h1>
                <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">Configure your service catalog and pricing.</p>
              </div>

            <div className={`rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-[var(--dash-shadow)] overflow-hidden ${editingService ? 'ring-2 ring-[var(--dash-accent)]' : ''}`}>
              <div className="p-6 border-b border-[var(--dash-border)]">
                <h2 className="text-lg font-semibold text-[var(--dash-text)]">
                  {editingService ? 'Edit Service' : 'Add New Service'}
                </h2>
                <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">
                  {editingService ? `Updating: ${editingService.name}` : 'Configure a new service for your catalog'}
                </p>
                {editingService && (
                  <div className="mt-2 p-2 rounded-lg bg-[var(--dash-accent)]/10 border border-[var(--dash-accent)]/30 text-xs text-[var(--dash-text)]">
                    Editing: Service ID {editingService.id}
                  </div>
                )}
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-[var(--dash-text)]">Service Name</Label>
                    <Input
                      value={newService.name}
                      onChange={(e) => setNewService(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Roof Installation"
                      className="h-10 rounded-lg border-[var(--dash-border)] bg-[var(--dash-bg)] text-[var(--dash-text)]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-[var(--dash-text)]">Service Type</Label>
                    <Select value={newService.serviceType} onValueChange={(value) => setNewService(prev => ({ ...prev, serviceType: value }))}>
                      <SelectTrigger className="h-10 rounded-lg border-[var(--dash-border)] bg-[var(--dash-bg)]">
                        <SelectValue placeholder="Select service type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fence">Fence Installation</SelectItem>
                        <SelectItem value="deck">Deck Construction</SelectItem>
                        <SelectItem value="roof">Roofing</SelectItem>
                        <SelectItem value="windows">Windows Installation</SelectItem>
                        <SelectItem value="gutters">Gutters & Downspouts</SelectItem>
                        <SelectItem value="siding">Siding Installation</SelectItem>
                        <SelectItem value="flooring">Flooring Installation</SelectItem>
                        <SelectItem value="painting">Painting Services</SelectItem>
                        <SelectItem value="electrical">Electrical Work</SelectItem>
                        <SelectItem value="plumbing">Plumbing Services</SelectItem>
                        <SelectItem value="concrete">Concrete Work</SelectItem>
                        <SelectItem value="landscaping">Landscaping</SelectItem>
                        <SelectItem value="hvac">HVAC Services</SelectItem>
                        <SelectItem value="other">Other Services</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-[var(--dash-text)]">Unit</Label>
                    <Select value={newService.unit} onValueChange={(value) => setNewService(prev => ({ ...prev, unit: value }))}>
                      <SelectTrigger className="h-10 rounded-lg border-[var(--dash-border)] bg-[var(--dash-bg)]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {MEASUREMENT_UNITS.map((unit) => (
                          <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-[var(--dash-text)]">Labor Rate ($)</Label>
                    <Input
                      type="number"
                      value={newService.laborRate}
                      onChange={(e) => setNewService(prev => ({ ...prev, laborRate: Number(e.target.value) }))}
                      placeholder="15.00"
                      className="h-10 rounded-lg border-[var(--dash-border)] bg-[var(--dash-bg)]"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button onClick={handleSaveService} className="h-10 rounded-lg bg-[var(--dash-accent)] hover:opacity-90 text-white">
                    {editingService ? 'Update Service' : 'Save Service'}
                  </Button>
                  {editingService && (
                    <Button onClick={resetForm} variant="outline" className="h-10 rounded-lg border-[var(--dash-border)]">
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-[var(--dash-shadow)] overflow-hidden">
              <div className="px-6 py-4 border-b border-[var(--dash-border)]">
                <h2 className="text-lg font-semibold text-[var(--dash-text)]">Current Services ({services.length})</h2>
                <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">Manage your service catalog and pricing</p>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[var(--dash-border)] hover:bg-transparent">
                      <TableHead className="text-[var(--dash-text-muted)] font-medium px-6 py-4">Service Name</TableHead>
                      <TableHead className="text-[var(--dash-text-muted)] font-medium px-6 py-4">Service Type</TableHead>
                      <TableHead className="text-[var(--dash-text-muted)] font-medium px-6 py-4">Unit</TableHead>
                      <TableHead className="text-[var(--dash-text-muted)] font-medium px-6 py-4 text-right">Labor Rate</TableHead>
                      <TableHead className="text-[var(--dash-text-muted)] font-medium px-6 py-4 text-center w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-12 text-[var(--dash-text-muted)]">
                          <div className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-6 w-6 border-2 border-[var(--dash-border)] border-t-[var(--dash-accent)]"></div>
                            Loading services…
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : services.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-12 text-[var(--dash-text-muted)]">
                          <p className="font-medium text-[var(--dash-text)]">No services configured yet</p>
                          <p className="text-sm mt-1">Add your first service above to get started.</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      services.map((service, index) => (
                        <TableRow key={service.id || service.value || index} className="border-[var(--dash-border)] hover:bg-slate-50/80">
                          <TableCell className="px-6 py-4 font-medium text-[var(--dash-text)]">{service.label || 'Unknown Service'}</TableCell>
                          <TableCell className="px-6 py-4">
                            <Badge variant="outline" className="dash-pill dash-pill-active">
                              {(service.value || service.serviceType || service.id || '').charAt(0).toUpperCase() + (service.value || service.serviceType || service.id || '').slice(1)}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-6 py-4 text-[var(--dash-text)]">{service.unit || 'unit'}</TableCell>
                          <TableCell className="px-6 py-4 text-right text-[var(--dash-text)] font-medium">${service.laborRate || 0}<span className="text-[var(--dash-text-muted)] text-sm font-normal">/{service.unit || 'unit'}</span></TableCell>
                          <TableCell className="px-6 py-4">
                            <div className="flex items-center justify-center gap-1">
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleEditService(service)} title="Edit"><Edit className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600 hover:text-red-700" onClick={() => showDeleteConfirmation(service)} title="Delete"><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialog.isOpen} onOpenChange={(open) => setDeleteDialog(prev => ({ ...prev, isOpen: open }))}>
              <AlertDialogContent className="border-[var(--dash-border)] bg-[var(--dash-card)]">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-[var(--dash-text)]">Delete Service</AlertDialogTitle>
                  <AlertDialogDescription className="text-[var(--dash-text-muted)]">
                    Are you sure you want to delete <strong className="text-[var(--dash-text)]">{deleteDialog.serviceName}</strong>? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-[var(--dash-border)]" onClick={() => setDeleteDialog(prev => ({ ...prev, isOpen: false }))}>Cancel</AlertDialogCancel>
                  <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmDelete}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}